import React, {useCallback, useMemo, useRef} from 'react';
import {Box, Button, Icon, Text, IconButton, Image, Input} from "@chakra-ui/react";
import {FileDown, ImagePlus, Pencil} from 'lucide-react';
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";
import {useApi} from "@/uses/useApi";

export const FileInput = ({file, alt, onChange, onRemove, accept, hasPreview, deleteText, ...props}) => {

    const api = useApi();
    const fileInputRef = useRef();
    const _hasPreview = hasPreview === true;
    const {t} = useLanguage();

    const handleImage = useCallback((e) => {
        const files = e.target.files;
        if (files.length !== 1) {
            throw new Error('INVALID_UPLOAD');
        }

        // Get a presigned url to allow uploading to miniIO from front
        api.FileService.getPresignedURL(files).then((res) => {
            if (res?.data?.type === 'success') {
                if (!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.presignedURL) {
                    throw new Error('MISSING_PRESIGNED_URL');
                }
                if (!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.url) {
                    throw new Error('MISSING_FILE_URL');
                }
                api.FileService.upload(res.data.value[0].presignedURL, files.item(0)).then(() => {
                    onChange(() => res?.data.value[0].url);
                }).catch(console.error)
            }
        });
    }, []);

    const fileName = useMemo(() => {
        if (!file) {
            return '';
        }
        if (typeof file !== 'string') {
            return file?.name ?? '';
        }
        try {
            return decodeURIComponent(file.split('/').pop());
        } catch (e) {
            return file;
        }
    }, [file]);

    const CustomIcon = useMemo(() => {
        return <IconButton
            bg="white"
            borderRadius=".75rem"
            size="md"
            onClick={(e) => {
                e.stopPropagation();
                fileInputRef?.current?.click?.();
            }}
            aria-label={t('sentences.uploadAFile')}
            {...(_hasPreview ? {
                position: 'absolute',
                bottom: '1.5rem',
                right: '.5rem',
                zIndex: 2
            } : {})}
        >
            <Icon 
                as={_hasPreview && file ? Pencil : (_hasPreview ? ImagePlus : FileDown)}
                boxSize={5} 
                color={_hasPreview ? "gray.700" : "gray.500"}
            />
        </IconButton>;
    }, [file, fileInputRef, _hasPreview])

    return (
        <Box
            position="relative"
            display="flex"
            mt="1.5rem"
            {...(!_hasPreview ? {
                p: '1rem 2rem',
                borderRadius: '1rem',
                gap: '1rem',
            } : {p: '0 1rem 2.5rem 0'})} {...props}>
            <Input
                ref={fileInputRef}
                type="file"
                accept={accept ?? "image/*"}
                onChange={handleImage}
                display="none"
            />
            {
                _hasPreview &&
                <Box
                    position="absolute"
                    borderRadius="1rem"
                    bg="gray.800"
                    top={0}
                    left={0}
                    bottom="2.5rem"
                    right="1rem"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    {...(!file ? {
                        cursor: "pointer",
                        onClick: () => fileInputRef?.current?.click?.()
                    } : {})}
                >
                    {file === null && <Text color="white" fontWeight="bold">{t('sentences.chooseAFile')}</Text>}
                    {(_hasPreview && file) &&
                        <Image src={file} alt={alt} w="100%" h="100%"
                               objectFit="cover" borderRadius=".5rem"/>
                    }
                </Box>
            }
            {
                !_hasPreview &&
                <Text
                    flex={1}
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                    alignSelf="center"
                >
                    {file ? fileName : t('sentences.chooseAFile')}
                </Text>
            }
            {
                file &&
                <Button
                    h="1.5rem"
                    fontSize="small"
                    variant="link"
                    color="white"
                    textDecoration="underline"
                    position="absolute"
                    bottom="0"
                    {...(_hasPreview ? {
                        left: 'calc(50% - 1rem)',
                        transform: 'translateX(-50%)'
                    } : {})}
                    onClick={(event) => {
                        event.preventDefault() && event.stopPropagation();
                        onChange(null);
                        if (fileInputRef?.current?.value) {
                            fileInputRef.current.value = '';
                        }
                    }}>
                    {deleteText ?? t('sentences.deleteFile')}
                </Button>
            }
            {
                CustomIcon
            }
        </Box>
    );
};

export default FileInput;
